import { getCurrencySymbol } from '@/constants/currencies'

/** Shown instead of a zero / missing amount (97% of deals have value 0, so `৳0` would be noise). */
export const NO_MONEY = '–'

/**
 * Money for the record lists: currency symbol + grouped number, the same text the deals list has
 * always shown for a non-zero value (`৳2,899`); an en dash for 0, null, undefined or non-finite.
 * A fractional amount always has exactly two decimals (`৳2,298.50`, never `৳2,298.5`): line items
 * are rounded to cents, so a percentage discount or tax leaves one. Whole amounts stay bare.
 * Pass the deal's currency or the org default; USD when none is given.
 */
export function formatMoney(value: number | null | undefined, currency?: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return NO_MONEY
  const number = Number.isInteger(value)
    ? value.toLocaleString('en-US')
    : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${getCurrencySymbol(currency || 'USD')}${number}`
}

/** Currencies written in lakh / crore (১ লাখ = 100,000) rather than K / M. */
const LAKH_CURRENCIES = new Set(['BDT', 'INR'])
const LAKH = 100_000
const CRORE = 10_000_000

const compactNumber = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })

/**
 * Money where space is tight (pipeline toolbar and column sums: `৳18.4L`): below 100,000 the full
 * `formatMoney` text; from there one decimal of lakh or crore for taka and rupees, K / M / B for
 * other currencies. An en dash for 0, null, undefined or non-finite.
 */
export function formatMoneyCompact(value: number | null | undefined, currency?: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value) || value === 0) return NO_MONEY
  const code = currency || 'USD'
  const abs = Math.abs(value)
  if (abs < LAKH) return formatMoney(value, code)
  const sign = value < 0 ? '-' : ''
  const symbol = getCurrencySymbol(code)
  if (LAKH_CURRENCIES.has(code)) {
    // Round first: 99.99 lakh would otherwise read "100L" instead of "1Cr".
    const lakhs = Math.round(abs / (LAKH / 10)) / 10
    if (lakhs < 100) return `${sign}${symbol}${lakhs}L`
    return `${sign}${symbol}${Math.round(abs / (CRORE / 10)) / 10}Cr`
  }
  return `${sign}${symbol}${compactNumber.format(abs)}`
}
